/**
 * ContactsTab — /phone v2's Contacts surface.
 *
 * Per PART 4:
 *   - Top row: search (name/number/fax/NPI), gold "Add contact",
 *     outlined "Import CSV".
 *   - Three cleanup cards: unnamed, duplicates, not-in-directory.
 *   - Type filters + Favorites.
 *   - Card grid, 3-across, with left border encoding contact type.
 *
 * Endpoints wrapped (existing):
 *   GET  /api/contacts                    (list)
 *   GET  /api/contacts/duplicates         (cleanup #2 — verified in
 *                                          contactsAPI)
 *   Batch-naming lands in /contacts?filter=unnamed
 *
 * Endpoints NOT wrapped (deliberately empty-stated per PART 8):
 *   "Not in directory" comparison (clients+staff w/o contact rows) — no
 *   endpoint exists. Card renders an honest gap message.
 */
import { useEffect, useMemo, useState } from "react";
import { contactsAPI } from "../../services/api";
import { formatPhoneNumber } from "../../lib/utils";
import { Users as UsersIcon, Phone, MessageSquare, Search, Ban, Pencil, Activity, X as XIcon, Clock, Star } from "lucide-react";
import EmptyState from "../../components/EmptyState";
import { NAVY, ROYAL, GOLD, GREEN, RED, AMBER, NAVY_TINT, NAVY_HAIRLINE } from "./tokens";
import ViewToggle, { useViewMode } from "./ViewToggle";
import CommsTimelinePanel from "../../components/healthcare/CommsTimelinePanel";
import SuggestedContactsStrip from "../../components/contacts/SuggestedContactsStrip";
import { useContactSlideover } from "../../components/contacts/ContactDetailSlideover";

function CleanupCard({ title, count, subtitle, cta, ctaHref, disabled, testid }) {
  return (
    <div className="rounded-xl border p-3 flex flex-col gap-1 bg-white" style={{ borderColor: NAVY_HAIRLINE }} data-testid={testid}>
      <div className="flex items-baseline gap-2">
        <div className="text-[24px] font-bold tabular-nums" style={{ color: NAVY }}>{count}</div>
        <div className="text-[12px] font-semibold" style={{ color: NAVY }}>{title}</div>
      </div>
      <div className="text-[11px] text-slate-500">{subtitle}</div>
      {ctaHref ? (
        <a
          href={ctaHref}
          className={`mt-1 h-8 self-start px-3 rounded-md text-[12px] font-semibold flex items-center ${disabled ? "opacity-40 pointer-events-none" : ""}`}
          style={{ background: NAVY, color: "white" }}
        >{cta}</a>
      ) : (
        <span className={`mt-1 h-8 self-start px-3 rounded-md text-[12px] font-semibold flex items-center ${disabled ? "bg-slate-200 text-slate-500" : "bg-slate-200 text-slate-500"}`}>{cta}</span>
      )}
    </div>
  );
}

const TYPE_LEFT_ACCENT = {
  client: ROYAL,
  caregiver: GREEN,
  provider: NAVY,
  clinic: NAVY,
  mco: AMBER,
  lead: "#B45309",
};

function initialsFor(c) {
  const f = (c.first_name || "").trim(), l = (c.last_name || "").trim();
  const ii = ((f[0] || "") + (l[0] || "")).toUpperCase();
  if (ii) return ii;
  const d = (c.phone_number || "").replace(/\D/g, "");
  return d.slice(-2) || "?";
}

function relTime(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return "";
  const diff = Date.now() - d.getTime();
  if (diff < 0) return "just now";
  const m = Math.floor(diff / 60000), h = Math.floor(diff / 3600000), day = Math.floor(diff / 86400000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  if (h < 24) return `${h}h ago`;
  if (day < 7) return `${day}d ago`;
  if (day < 30) return `${Math.floor(day / 7)}w ago`;
  if (day < 365) return `${Math.floor(day / 30)}mo ago`;
  return `${Math.floor(day / 365)}y ago`;
}

function ContactCard({ c, onCall, onText, onEdit, onTimeline }) {
  const type = (c.contact_type || "").toLowerCase();
  const accent = TYPE_LEFT_ACCENT[type] || NAVY;
  const stopBlocked = c.sms_opt_out === true || c.consent_status === "opted_out";
  const fav = c.is_favorite === true || c.favorite === true;
  const name = `${c.first_name || ""} ${c.last_name || ""}`.trim() || "Unnamed contact";
  const activityTs = c.last_contacted_at || c.last_message_at || c.updated_at || c.created_at;
  const activityLabel = relTime(activityTs);
  const activityIsComms = !!(c.last_contacted_at || c.last_message_at);

  return (
    <div
      className="rounded-2xl bg-white border p-3.5 transition-shadow hover:shadow-md"
      style={{ borderColor: NAVY_HAIRLINE, borderLeft: `4px solid ${accent}` }}
      data-testid={`comms2-contact-${c.id}`}
    >
      {/* Header — avatar w/ contact-type accent ring, name, type chip */}
      <div className="flex items-start gap-3">
        <div
          className="w-11 h-11 rounded-full grid place-items-center text-[13px] font-bold shrink-0"
          style={{ background: `${accent}14`, color: accent, boxShadow: `0 0 0 2px #fff, 0 0 0 3.5px ${accent}55` }}
          data-testid={`comms2-contact-avatar-${c.id}`}
        >
          {initialsFor(c)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <div className="text-[13.5px] font-bold truncate" style={{ color: NAVY }}>{name}</div>
            {fav && <Star className="w-3.5 h-3.5 shrink-0" style={{ color: GOLD, fill: GOLD }} data-testid={`comms2-contact-fav-${c.id}`} />}
          </div>
          <div className="mt-1 flex items-center gap-1.5 flex-wrap">
            {c.contact_type && (
              <span className="text-[9.5px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded" style={{ background: `${accent}14`, color: accent }}>
                {c.contact_type}
              </span>
            )}
            <span className="text-[11px] text-slate-500 truncate">
              {[c.affiliation || c.mco || c.company, c.chart_number || c.npi].filter(Boolean).join(" · ")}
            </span>
          </div>
        </div>
      </div>

      {/* Numbers */}
      <div className="mt-2.5 text-[12px] text-slate-700 font-mono">{formatPhoneNumber(c.phone_number) || "—"}</div>
      {c.fax_number && <div className="text-[11px] text-slate-500 font-mono">Fax {formatPhoneNumber(c.fax_number)}</div>}

      {/* Last-activity badge + consent/shared-line status */}
      <div className="mt-2 flex items-center gap-2 flex-wrap">
        {activityLabel && (
          <span
            className="inline-flex items-center gap-1 text-[10.5px] font-semibold px-2 py-0.5 rounded-full"
            style={{ background: NAVY_TINT, color: "#64748B" }}
            title={activityIsComms ? "Last contacted" : "Record last updated"}
            data-testid={`comms2-contact-activity-${c.id}`}
          >
            <Clock className="w-3 h-3" /> {activityIsComms ? "Active" : "Updated"} {activityLabel}
          </span>
        )}
        {c.shared_line && <span className="text-[10.5px] font-semibold" style={{ color: AMBER }}>⚠ shared landline</span>}
        {stopBlocked && <span className="text-[10.5px] font-semibold" style={{ color: RED }}>Replied STOP · texts blocked</span>}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1.5 mt-2.5 pt-2.5 border-t" style={{ borderColor: NAVY_HAIRLINE }}>
        <button
          onClick={() => onCall(c)}
          className="h-8 px-2.5 rounded-md text-white text-[11.5px] font-semibold flex items-center gap-1 hover:brightness-95"
          style={{ background: GREEN }}
          disabled={!c.phone_number}
          data-testid={`comms2-contact-call-${c.id}`}
        >
          <Phone className="w-3.5 h-3.5" /> Call
        </button>
        <button
          onClick={() => !stopBlocked && onText(c)}
          disabled={stopBlocked || !c.phone_number}
          className="h-8 px-2.5 rounded-md border text-[11.5px] font-medium flex items-center gap-1 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ borderColor: NAVY_HAIRLINE, color: NAVY }}
          data-testid={`comms2-contact-text-${c.id}`}
          title={stopBlocked ? "Recipient opted out via STOP" : "Text"}
        >
          {stopBlocked ? <Ban className="w-3.5 h-3.5" /> : <MessageSquare className="w-3.5 h-3.5" />}
          Text
        </button>
        <button
          onClick={() => onEdit && onEdit(c)}
          className="h-8 px-2.5 rounded-md border text-[11.5px] font-medium flex items-center gap-1 hover:bg-slate-50 ml-auto"
          style={{ borderColor: NAVY_HAIRLINE, color: NAVY }}
          data-testid={`comms2-contact-edit-${c.id}`}
          title="Edit contact"
        >
          <Pencil className="w-3.5 h-3.5" />
          Edit
        </button>
        <button
          onClick={() => onTimeline && onTimeline(c)}
          className="h-8 px-2.5 rounded-md border text-[11.5px] font-medium flex items-center gap-1 hover:bg-slate-50"
          style={{ borderColor: NAVY_HAIRLINE, color: NAVY }}
          data-testid={`comms2-contact-timeline-${c.id}`}
          title="Communication history"
        >
          <Activity className="w-3.5 h-3.5" />
          History
        </button>
      </div>
    </div>
  );
}

/* ── Compact single-row rendering for list view (iter 489) ── */
function ContactRow({ c, onCall, onText, onEdit, onTimeline, isLast }) {
  const type = (c.contact_type || "").toLowerCase();
  const accent = TYPE_LEFT_ACCENT[type] || NAVY_HAIRLINE;
  const stopBlocked = c.sms_opt_out === true || c.consent_status === "opted_out";
  return (
    <div
      className={`grid grid-cols-[4px_minmax(0,1.6fr)_minmax(0,1fr)_100px_215px] items-center gap-3 px-3 py-2 bg-white text-[12.5px] ${isLast ? "" : "border-b"}`}
      style={{ borderColor: NAVY_HAIRLINE }}
      data-testid={`comms2-contact-row-${c.id}`}
    >
      <div className="h-6 rounded-sm" style={{ background: accent }} />
      <div className="min-w-0">
        <div className="font-semibold truncate" style={{ color: NAVY }}>
          {c.first_name || ""} {c.last_name || <span className="italic text-slate-400">unnamed</span>}
        </div>
        <div className="text-[10.5px] text-slate-500 truncate">
          {[c.contact_type, c.affiliation || c.mco || c.company, c.chart_number || c.npi].filter(Boolean).join(" · ") || "—"}
        </div>
      </div>
      <div className="text-[11.5px] text-slate-700 font-mono truncate">
        {formatPhoneNumber(c.phone_number) || "—"}
        {c.fax_number ? <span className="ml-1 text-[10px] text-slate-400">· fax {formatPhoneNumber(c.fax_number)}</span> : null}
      </div>
      <div className="text-[10.5px]">
        {stopBlocked && <span className="font-semibold" style={{ color: RED }}>STOP-blocked</span>}
        {!stopBlocked && c.shared_line && <span style={{ color: AMBER }}>Shared line</span>}
      </div>
      <div className="flex items-center justify-end gap-1.5">
        <button
          onClick={() => onCall(c)}
          disabled={!c.phone_number}
          className="h-7 px-2 rounded text-white text-[11px] font-semibold flex items-center gap-1 hover:brightness-95 disabled:opacity-40"
          style={{ background: GREEN }}
          data-testid={`comms2-contact-row-call-${c.id}`}
        >
          <Phone className="w-3 h-3" /> Call
        </button>
        <button
          onClick={() => !stopBlocked && onText(c)}
          disabled={stopBlocked || !c.phone_number}
          className="h-7 px-2 rounded border text-[11px] font-medium flex items-center gap-1 hover:bg-slate-50 disabled:opacity-40"
          style={{ borderColor: NAVY_HAIRLINE, color: NAVY }}
          data-testid={`comms2-contact-row-text-${c.id}`}
          title={stopBlocked ? "Recipient opted out via STOP" : "Text"}
        >
          {stopBlocked ? <Ban className="w-3 h-3" /> : <MessageSquare className="w-3 h-3" />}
          Text
        </button>
        <button
          onClick={() => onEdit && onEdit(c)}
          className="h-7 w-7 rounded border flex items-center justify-center hover:bg-slate-50"
          style={{ borderColor: NAVY_HAIRLINE, color: NAVY }}
          data-testid={`comms2-contact-row-edit-${c.id}`}
          title="Edit contact"
        >
          <Pencil className="w-3 h-3" />
        </button>
        <button
          onClick={() => onTimeline && onTimeline(c)}
          className="h-7 w-7 rounded border flex items-center justify-center hover:bg-slate-50"
          style={{ borderColor: NAVY_HAIRLINE, color: NAVY }}
          data-testid={`comms2-contact-row-timeline-${c.id}`}
          title="Communication history"
        >
          <Activity className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

export default function ContactsTab({ onCall, onText, navigate }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dupCount, setDupCount] = useState(null); // null = unknown / gap
  const [gapCount, setGapCount] = useState(null); // null = endpoint unavailable
  // Iter 508j (Dawn 2026-02-25) — opinionated default flip.
  //   OLD: `typeFilter = "all"` dumped every unnamed inbound number
  //        into the main grid, making the tab look like a 1998 phone
  //        book with the 40+ "one-off caller" rows drowning the
  //        actual directory.
  //   NEW: default = "named". Only rows with a first_name OR last_name
  //        land in the main view. Unnamed sits behind its own pill
  //        (surfaces as a task queue, not part of the directory).
  const [typeFilter, setTypeFilter] = useState("named");
  const [q, setQ] = useState("");
  const [view, setView] = useViewMode("comms2-contacts-view", "cards");

  // Iter 505 — the /phone workspace didn't expose ANY edit path for
  // contacts. Users had to leave to /contacts and hunt through the
  // company-tabbed picker to fix a typo. We use window.location.assign
  // rather than react-router `navigate` because the page nav (with
  // ?edit query) needs a fresh mount of PhoneBookPanel to pick up the
  // deep-link on its first render — a client-side transition would
  // leave PhoneBookPanel showing its previous state.
  // Iter 505 — the /phone workspace didn't expose ANY edit path for
  // contacts. Users had to leave to /contacts and hunt through the
  // company-tabbed picker to fix a typo. We deep-link to the legacy
  // /contacts page (via ?legacy=1 so LegacyOrRedirect stays out of
  // the way) — PhoneBookPanel there reads ?edit=<id> on first render
  // and pops the full edit dialog straight away.
  const onEdit = (c) => {
    if (!c?.id) return;
    window.location.assign(`/contacts?legacy=1&edit=${encodeURIComponent(c.id)}`);
  };

  // Iter 506 · Contact Timeline slideover — clicking History on any
  // contact opens a slideover that fetches the merged SMS+call+VM+fax
  // feed via /api/contact-registry/{id}/timeline. All channels resolve
  // through the identity spine so timeline events point to real people.
  //
  // Iter 508j — routes through the canonical ContactDetailSlideover
  // (mounted app-wide in MainLayout). Kept the local `timelineFor`
  // state around for the fallback CommsTimelinePanel modal used by
  // legacy call sites that don't yet consume the hook.
  const { open: openContactSlideover } = useContactSlideover();
  const [timelineFor, setTimelineFor] = useState(null);
  const onTimeline = (c) => {
    if (!c?.id) return;
    openContactSlideover({ contactId: c.id });
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await contactsAPI.getAll();
        if (alive) setContacts(r.data || []);
      } catch { if (alive) setContacts([]); }
      finally { if (alive) setLoading(false); }
    })();
    // Duplicates — the /contacts/duplicates route is the canonical
    // cleanup surface. We only need the count here; the full review
    // lives in the existing /contacts page.
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contacts/duplicates?limit=1`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
        });
        if (res.ok) {
          const data = await res.json();
          if (alive) setDupCount(Array.isArray(data) ? data.length : (data.total ?? data.count ?? null));
        }
      } catch { /* honest gap → dupCount stays null */ }
    })();
    // Directory gaps — patients + staff NOT in the shared contact
    // directory. Powers cleanup card #3.
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contacts/gaps?limit=500`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
        });
        if (res.ok) {
          const data = await res.json();
          if (alive) setGapCount(typeof data.count === "number" ? data.count : (Array.isArray(data.items) ? data.items.length : 0));
        }
      } catch { /* endpoint gap → count stays null */ }
    })();
    return () => { alive = false; };
  }, []);

  const filtered = useMemo(() => {
    let src = contacts;
    // Iter 508j — new "named" and "unnamed" pseudo-filters. "named"
    // shows anyone with a first_name OR last_name (the actual
    // directory). "unnamed" is the cleanup queue for auto-created
    // rows from unknown inbound events.
    if (typeFilter === "named") {
      src = src.filter(c => !!(c.first_name || c.last_name));
    } else if (typeFilter === "unnamed") {
      src = src.filter(c => !c.first_name && !c.last_name);
    } else if (typeFilter === "favorites") {
      src = src.filter(c => c.favorite === true);
    } else if (typeFilter !== "all") {
      src = src.filter(c => (c.contact_type || "").toLowerCase() === typeFilter);
    }
    if (q.trim()) {
      const term = q.trim().toLowerCase();
      const digits = term.replace(/\D/g, "");
      src = src.filter(c => {
        const name = `${c.first_name || ""} ${c.last_name || ""}`.toLowerCase();
        if (name.includes(term)) return true;
        if ((c.npi || "").toLowerCase().includes(term)) return true;
        // Only match phone/fax when the query actually has digits —
        // otherwise `"".includes("")` is always true and the search
        // returns every contact (i.e. appears to do nothing).
        if (digits) {
          if ((c.phone_number || "").replace(/\D/g, "").includes(digits)) return true;
          if ((c.fax_number || "").replace(/\D/g, "").includes(digits)) return true;
        }
        return false;
      });
    }
    return src;
  }, [contacts, typeFilter, q]);

  const unnamedCount = contacts.filter(c => !c.first_name && !c.last_name).length;
  const namedCount = contacts.length - unnamedCount;
  // Iter 508j — reordered so the opinionated defaults surface first.
  const types = ["named", "unnamed", "client", "caregiver", "provider", "clinic", "mco", "lead", "favorites", "all"];
  const typeCount = (t) => {
    if (t === "all") return contacts.length;
    if (t === "named") return namedCount;
    if (t === "unnamed") return unnamedCount;
    if (t === "favorites") return contacts.filter(c => c.favorite).length;
    return contacts.filter(c => (c.contact_type || "").toLowerCase() === t).length;
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-slate-50">
      {/* Top row */}
      <div className="shrink-0 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 px-3 sm:px-4 py-3 border-b bg-white" style={{ borderColor: NAVY_HAIRLINE }}>
        <div className="flex-1 max-w-lg flex items-center gap-1.5 rounded-md border h-9 px-3" style={{ borderColor: NAVY_HAIRLINE, background: "white" }}>
          <Search className="w-3.5 h-3.5 opacity-60" style={{ color: NAVY }} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, number, fax, NPI"
            className="flex-1 text-[13px] bg-transparent outline-none"
            data-testid="comms2-contacts-search"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ViewToggle view={view} setView={setView} testid="comms2-contacts" />
          <a
            href="/contacts?legacy=1&action=add"
            className="h-10 px-4 sm:px-5 rounded-lg text-[13px] sm:text-[14px] font-bold hover:brightness-95 flex items-center gap-1.5 shrink-0"
            style={{ background: GOLD, color: NAVY }}
            data-testid="comms2-add-contact"
          >
            Add contact
          </a>
          <a
            href="/contacts?legacy=1&action=import"
            className="h-10 px-3 sm:px-4 rounded-lg border text-[13px] font-medium hover:bg-white flex items-center shrink-0"
            style={{ borderColor: NAVY_HAIRLINE, color: NAVY }}
            data-testid="comms2-import-csv"
          >
            Import CSV
          </a>
        </div>
      </div>

      {/* Cleanup cards */}
      <div className="px-4 pt-4">
        {/* Iter 508j · Suggested Contacts. Shows unknown numbers with
            ≥3 inbound events / 30d, with a Claude Haiku 4.5 name-guess
            from SMS body content. Hedged "Possibly …" language — the
            RN must click Save to promote. Empty state renders nothing
            so the strip doesn't add noise on quiet days. */}
        <div className="mb-3">
          <SuggestedContactsStrip onContactSaved={() => {
            // Reload contacts so a fresh save shows up in the grid.
            contactsAPI.getAll().then(r => setContacts(r.data || [])).catch(() => {});
          }} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <CleanupCard
            title="unnamed"
            count={unnamedCount}
            subtitle="Created from inbound calls and texts"
            cta="Batch name"
            ctaHref="/contacts?filter=unnamed"
            testid="comms2-cleanup-unnamed"
          />
          {/* Iter 508j — cleanup cards only render when their backend
              endpoint exists. Dawn (correctly) flagged "Endpoint
              unavailable" chips as noise (same class of defect she
              called out on the old Text Blast Recent-Blasts panel). */}
          {dupCount !== null && (
            <CleanupCard
              title="possible duplicates"
              count={dupCount}
              subtitle="Same name, different record · shared-line matches NEVER auto-merged"
              cta="Review"
              ctaHref="/contacts?filter=duplicates"
              testid="comms2-cleanup-duplicates"
            />
          )}
          {gapCount !== null && (
            <CleanupCard
              title="not in directory"
              count={gapCount}
              subtitle={gapCount === 0
                ? "All clients and staff have contact rows — nice."
                : "Clients + staff whose phone numbers aren't in the shared directory yet"}
              cta={gapCount === 0 ? "All caught up" : "Review"}
              ctaHref={gapCount === 0 ? null : "/contacts?filter=gaps"}
              disabled={gapCount === 0}
              testid="comms2-cleanup-gaps"
            />
          )}
        </div>

        {/* Type filters */}
        <div className="flex items-center gap-2 flex-wrap mb-3">
          {types.map(t => (
            <button
              key={t}
              onClick={() => setTypeFilter(t)}
              className="h-8 px-3 rounded-full text-[12px] font-medium border"
              style={typeFilter === t
                ? { background: NAVY, color: "white", borderColor: NAVY }
                : { background: "transparent", color: NAVY, borderColor: NAVY_HAIRLINE }}
              data-testid={`comms2-contacts-type-${t}`}
            >
              {t === "all" ? `All ${typeCount("all")}` : `${t[0].toUpperCase()}${t.slice(1)} ${typeCount(t)}`}
            </button>
          ))}
        </div>
      </div>

      {/* Grid or List */}
      <div className="flex-1 overflow-y-auto px-3 sm:px-4 pb-4">
        {loading && <div className="text-slate-400 text-center py-8 text-[13px]">Loading…</div>}
        {!loading && filtered.length === 0 && (
          <EmptyState
            icon={UsersIcon}
            title="No contacts here"
            hint="Contacts you save or message will appear in this list."
            testId="contacts-empty"
          />
        )}
        {view === "cards" && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {filtered.slice(0, 60).map(c => (
              <ContactCard key={c.id} c={c} onCall={onCall} onText={onText} onEdit={onEdit} onTimeline={onTimeline} />
            ))}
          </div>
        )}
        {view === "list" && (
          <div className="rounded-lg border overflow-hidden bg-white" style={{ borderColor: NAVY_HAIRLINE }} data-testid="comms2-contacts-list">
            <div className="grid grid-cols-[4px_minmax(0,1.6fr)_minmax(0,1fr)_100px_215px] text-[10.5px] uppercase font-semibold tracking-wider text-slate-500 px-3 py-1.5 border-b" style={{ borderColor: NAVY_HAIRLINE, background: NAVY_TINT }}>
              <span />
              <span>Name</span>
              <span>Number</span>
              <span>Status</span>
              <span className="text-right">Actions</span>
            </div>
            {filtered.slice(0, 60).map((c, i, arr) => (
              <ContactRow key={c.id} c={c} onCall={onCall} onText={onText} onEdit={onEdit} onTimeline={onTimeline} isLast={i === arr.length - 1} />
            ))}
          </div>
        )}
        {filtered.length > 60 && (
          <div className="text-center text-[11px] text-slate-500 pt-3">
            Showing 60 of {filtered.length} — refine search or type filter to narrow.
          </div>
        )}
      </div>

      {/* Iter 506 · Contact Timeline slideover — one merged feed for
          one person: SMS, calls, voicemails, faxes. Powered by the
          existing spine endpoint /api/contact-registry/{id}/timeline. */}
      {timelineFor && (
        <ContactTimelineSlideover
          contact={timelineFor}
          onClose={() => setTimelineFor(null)}
        />
      )}
    </div>
  );
}

// ── Contact Timeline Slideover ────────────────────────────────────
// Right-hand slideover panel that shows every SMS, call, voicemail,
// and fax we've ever exchanged with one contact — the "one timeline
// per person regardless of channel" requirement.
function ContactTimelineSlideover({ contact, onClose }) {
  const name = `${contact.first_name || ""} ${contact.last_name || ""}`.trim() || "Contact";
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="comms2-contact-timeline-slideover">
      <div
        className="flex-1 bg-slate-900/40"
        onClick={onClose}
        data-testid="comms2-contact-timeline-backdrop"
      />
      <div className="w-full sm:w-[500px] max-w-full bg-white shadow-2xl flex flex-col">
        <div
          className="flex items-center justify-between px-4 py-3 border-b"
          style={{ borderColor: NAVY_HAIRLINE, background: NAVY_TINT }}
        >
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Communications</div>
            <div className="text-[15px] font-bold truncate" style={{ color: NAVY }}>{name}</div>
            {contact.phone_number && (
              <div className="text-[12px] text-slate-500">{formatPhoneNumber(contact.phone_number)}</div>
            )}
          </div>
          <button
            onClick={onClose}
            className="h-8 w-8 rounded-md flex items-center justify-center hover:bg-white"
            style={{ color: NAVY }}
            data-testid="comms2-contact-timeline-close"
            title="Close"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          <CommsTimelinePanel contactId={contact.id} days={365} />
        </div>
      </div>
    </div>
  );
}
